'use client';

import Link from 'next/link';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, BadgeCheck, Info } from 'lucide-react';
import { LiquidSurface } from '@repo/ui/components/liquid-surface';
import { Skeleton } from '@repo/ui/components/skeleton';
import { track } from '@/lib/analytics';

import type { HealthImprovement } from '../utils/derive-health';

/**
 * What the panel may say when it has no card to show. Only `all-clear`
 * earns the green confirmation; the others are honest about why the list
 * is empty.
 */
export type ImprovementsEmptyReason =
  /** Every check with data is inside its guideline. */
  | 'all-clear'
  /** Something is outside its guideline, with no specific change to model. */
  | 'attention'
  /** A check carries a result this build cannot read: no claim either way. */
  | 'unrecognised'
  /** No check has data yet, so nothing was assessed either way. */
  | 'unassessed'
  /** The data the suggestions are built from did not load. */
  | 'unavailable';

interface PortfolioImprovementsProps {
  improvements: HealthImprovement[];
  isLoading?: boolean;
  /** Why the list is empty, when it is. */
  emptyReason?: ImprovementsEmptyReason;
}

const EMPTY_COPY: Record<
  Exclude<ImprovementsEmptyReason, 'all-clear'>,
  { title: string; detail: string }
> = {
  attention: {
    title: 'No specific change to suggest right now',
    detail:
      'Some checks are not fully inside their guidelines. Look at the areas above to see which ones.'
  },
  unrecognised: {
    title: 'Nothing to suggest right now',
    detail:
      'Some checks returned a result this page cannot read yet, so it cannot confirm that everything is inside its guideline. Look at the areas above for each result.'
  },
  unassessed: {
    title: 'Nothing to suggest yet',
    detail:
      'Your checks are still waiting on data. Suggestions appear here once they have it. Nothing for you to do.'
  },
  unavailable: {
    title: 'We could not check for improvements',
    detail:
      'Some of the data behind these suggestions did not load. Use Retry portfolio data at the top of the page.'
  }
};

function ImpactChip({ improvement }: { improvement: HealthImprovement }) {
  return (
    <LiquidSurface
      type="control"
      className="rounded-rowa-md text-rowa-label-sm text-rowa-text-brand flex h-[30px] w-[92px] shrink-0 items-center justify-center font-medium"
    >
      {improvement.impactLabel ??
        (improvement.scoreDelta != null
          ? `Score +${improvement.scoreDelta}`
          : improvement.driverDelta != null
            ? 'Modeled'
            : 'Evidence')}
    </LiquidSurface>
  );
}

/**
 * One modeled improvement (Figma "ROWA / Portfolio Improvement Row /
 * Liquid", node 1865:1904): impact chip · title / detail / result+trade-off
 * stack · review action. Never implies immediate execution.
 */
function ImprovementRow({
  improvement,
  index
}: {
  improvement: HealthImprovement;
  index: number;
}) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.li
      initial={reducedMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: 'easeOut' }}
      className="border-rowa-border-subtle flex min-h-[76px] items-center gap-6 border-b py-3 last:border-0"
    >
      <ImpactChip improvement={improvement} />

      <div className="min-w-0 flex-1">
        <p className="text-rowa-body-md text-rowa-text-primary leading-5 font-medium">
          {improvement.title}
        </p>
        <p className="text-rowa-label-sm text-rowa-text-secondary mt-[3px] font-normal">
          {improvement.detail}
        </p>
        <div className="mt-[3px] flex flex-wrap items-baseline gap-x-6 gap-y-0.5 sm:grid sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
          <p className="text-rowa-label-sm text-rowa-text-brand font-medium">
            {improvement.driverDelta
              ? `${improvement.driverDelta.label} ${improvement.driverDelta.from} → ${improvement.driverDelta.to}`
              : (improvement.resultLabel ?? 'Evidence repair')}
          </p>
          <p className="text-rowa-label-sm text-rowa-text-secondary font-normal">
            What it costs · {improvement.tradeoff}
          </p>
        </div>
      </div>

      <Link
        href={improvement.href}
        onClick={() =>
          track('overview.improvement_actioned', {
            improvement: improvement.id
          })
        }
        className="text-rowa-label-md text-rowa-text-brand inline-flex shrink-0 items-center gap-1 self-center font-medium whitespace-nowrap hover:opacity-80"
      >
        {improvement.actionLabel}
        <ArrowRight className="size-3.5" aria-hidden />
      </Link>
    </motion.li>
  );
}

/**
 * Improve portfolio health (O03, node 1792:558): guideline breaches ranked
 * by urgency, each with its measurement, trade-off, and a review action.
 * Cards never imply execution.
 */
export function PortfolioImprovements({
  improvements,
  isLoading = false,
  emptyReason = 'all-clear'
}: PortfolioImprovementsProps) {
  return (
    <LiquidSurface
      type="panel"
      id="improvements"
      role="region"
      aria-labelledby="portfolio-improvements-title"
      className="scroll-mt-24 p-6"
    >
      <h2
        id="portfolio-improvements-title"
        className="text-rowa-card text-rowa-text-primary"
      >
        Improve portfolio health
      </h2>
      <p className="text-rowa-body-sm text-rowa-text-secondary mt-1">
        Where your portfolio sits outside its guidelines today, ranked by
        urgency. Review what each change costs before acting. Nothing here moves
        funds.
      </p>

      {isLoading ? (
        <div className="mt-5 space-y-3">
          <Skeleton className="h-[68px] w-full" />
          <Skeleton className="h-[68px] w-full" />
          <Skeleton className="h-[68px] w-full" />
        </div>
      ) : improvements.length === 0 && emptyReason !== 'all-clear' ? (
        <div className="mt-5 flex items-start gap-3">
          <Info
            className="text-rowa-text-tertiary mt-0.5 size-5 shrink-0"
            aria-hidden
          />
          <div>
            <p className="text-rowa-body-md text-rowa-text-primary font-medium">
              {EMPTY_COPY[emptyReason].title}
            </p>
            <p className="text-rowa-body-sm text-rowa-text-secondary mt-0.5">
              {EMPTY_COPY[emptyReason].detail}
            </p>
          </div>
        </div>
      ) : improvements.length === 0 ? (
        <div className="mt-5 flex items-start gap-3">
          <BadgeCheck
            className="text-rowa-status-success mt-0.5 size-5 shrink-0"
            aria-hidden
          />
          <div>
            <p className="text-rowa-body-md text-rowa-text-primary font-medium">
              Nothing to improve right now
            </p>
            <p className="text-rowa-body-sm text-rowa-text-secondary mt-0.5">
              Every check with data today is inside its guideline.
            </p>
          </div>
        </div>
      ) : (
        <ul className="mt-2 list-none">
          {improvements.map((improvement, index) => (
            <ImprovementRow
              key={improvement.id}
              improvement={improvement}
              index={index}
            />
          ))}
        </ul>
      )}
    </LiquidSurface>
  );
}
